import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import type { Invite } from '@ankor-io/common/types/account'

import { Button } from '@/ui-components/Button'
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@/ui-components/Dialog'

type RevokeInviteModalProps = {
  isOpen: boolean
  onClose: () => void
  selectedInvite: Invite
  onSubmit: (invite: Invite) => void
}

const RevokeInviteModal = ({ isOpen, onClose, selectedInvite, onSubmit }: RevokeInviteModalProps) => {
  const [isRevoking, setIsRevoking] = useState(false)

  useEffect(() => {
    setIsRevoking(false)
  }, [isOpen])

  const handleSubmit = () => {
    if (!selectedInvite) {
      return
    }

    setIsRevoking(true)
    onSubmit(selectedInvite)
  }

  const handleClose = () => {
    setIsRevoking(false)
    onClose()
  }

  if (!selectedInvite) {
    return null
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-red-600 dark:text-red-400" />
          <DialogTitle>Revoke Invite</DialogTitle>
        </div>
        <Button
          plain
          onClick={handleClose}
        >
          <X data-slot="icon" />
        </Button>
      </div>

      <DialogBody>
        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-200">Are you sure you want to revoke this invitation?</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This action cannot be undone. The invited user will no longer be able to accept this invitation.
          </p>
        </div>
      </DialogBody>

      <DialogActions>
        <Button
          plain
          onClick={handleClose}
          disabled={isRevoking}
        >
          Cancel
        </Button>
        <Button
          color="red"
          className="data-disabled:opacity-50 data-disabled:cursor-not-allowed"
          disabled={isRevoking}
          onClick={handleSubmit}
          data-testid="revoke-invite-confirm"
        >
          {isRevoking ? 'Revoking...' : 'Revoke Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RevokeInviteModal
